import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import superAdminService from '../../services/superAdminService';
import stripeService from '../../services/stripeService';
import adminService from '../../services/adminService';
import authService from '../../services/authService';

// Material UI components
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Avatar,
  IconButton,
  Alert,
  Snackbar,
  Skeleton,
  Tabs,
  Tab,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemIcon,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Tooltip,
  Switch,
  FormControlLabel,
  Breadcrumbs,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Backdrop
} from '@mui/material';

// Material UI icons
import {
  Edit as EditIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
  Schedule as ClockIcon,
  Visibility as EyeIcon,
  Person as PersonIcon,
  Storage as StorageIcon,
  Receipt as ReceiptIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  AttachMoney as MoneyIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as LanguageIcon,
  DateRange as DateRangeIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  PauseCircleOutline as PauseCircleOutlineIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  PictureAsPdf as PictureAsPdfIcon
} from '@mui/icons-material';

// Import PDF utilities
import { generatePdfFromElement } from '../../utils/pdfUtils';
import OrgFinancialReport from '../../components/reports/OrgFinancialReport';

// Tab panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tenant-tabpanel-${index}`}
      aria-labelledby={`tenant-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Status badge component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    active: {
      color: 'success',
      icon: <CheckCircleIcon fontSize="small" />
    },
    pending: {
      color: 'warning',
      icon: <ClockIcon fontSize="small" />
    },
    suspended: {
      color: 'error',
      icon: <CancelIcon fontSize="small" />
    },
    default: {
      color: 'default',
      icon: <WarningIcon fontSize="small" />
    }
  };

  const config = statusConfig[status] || statusConfig.default;

  return (
    <Chip
      icon={config.icon}
      label={status || 'unknown'}
      color={config.color}
      size="small"
      variant="outlined"
    />
  );
};

// Add the getPlanColors function
const getPlanColors = (planId) => {
  const colors = {
    basic: {
      main: '#757575',
      light: 'rgba(117, 117, 117, 0.1)',
      text: '#757575'
    },
    professional: {
      main: '#2196f3',
      light: 'rgba(33, 150, 243, 0.1)',
      text: '#2196f3'
    },
    enterprise: {
      main: '#673ab7',
      light: 'rgba(103, 58, 183, 0.1)',
      text: '#673ab7'
    },
    custom: {
      main: '#f44336',
      light: 'rgba(244, 67, 54, 0.1)',
      text: '#f44336'
    }
  };

  return colors[planId] || colors.basic;
};

// Add the getPlanName function
const getPlanName = (planId) => {
  const names = {
    basic: 'Basic Plan',
    professional: 'Professional Plan',
    enterprise: 'Enterprise Plan',
    custom: 'Custom Plan'
  };

  return names[planId] || 'Unknown Plan';
};

const SuperAdminTenantDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [usageData, setUsageData] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [adminUsers, setAdminUsers] = useState([]);
  const [memberCount, setMemberCount] = useState(0);
  const [authChecked, setAuthChecked] = useState(false);
  const [tenantStats, setTenantStats] = useState(null);
  
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [adminToReset, setAdminToReset] = useState(null);
  const [adminStatusDialogOpen, setAdminStatusDialogOpen] = useState(false);
  const [adminToChangeStatus, setAdminToChangeStatus] = useState(null);
  const [newAdminStatus, setNewAdminStatus] = useState('active');
  const [tempPassword, setTempPassword] = useState('');
  
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [planChangeNote, setPlanChangeNote] = useState('');
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsData, setDetailsData] = useState(null);
  const [cacheInfo, setCacheInfo] = useState(null);

  const [availablePlans, setAvailablePlans] = useState([]);

  const [financialData, setFinancialData] = useState(null);
  const [loadingFinancials, setLoadingFinancials] = useState(false);
  const financialReportRef = useRef(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printMenuAnchor, setPrintMenuAnchor] = useState(null);
  const [printReportType, setPrintReportType] = useState('all');
  const [printDialogOpen, setPrintDialogOpen] = useState(false);

  const loadTenantDetailsOptimized = async () => {
    try {
      setLoadingDetails(true);
      console.log(`Loading optimized tenant data for ID: ${id}`);
      
      const response = await superAdminService.getTenantDetailsAll(id);
      
      if (response && response.data) {
        setDetailsData(response.data);
        
        if (response.data.tenant) {
          setTenant(response.data.tenant);
          setAdminNotes(response.data.tenant.adminNotes || '');
        }
        
        if (response.data.usage) {
          setUsageData(response.data.usage);
        }
        
        if (response.data.subscription) {
          setSubscriptionData(response.data.subscription);
        }
        
        if (response.data.memberCount !== undefined) {
          console.log('Setting member count to:', response.data.memberCount);
          setMemberCount(response.data.memberCount);
        } else if (response.data.stats?.memberCount !== undefined) {
          console.log('Setting member count from stats:', response.data.stats.memberCount);
          setMemberCount(response.data.stats.memberCount);
        }
        
        if (response._cachedAt) {
          setCacheInfo({
            timestamp: response._cachedAt,
            formattedTime: new Date(response._cachedAt).toLocaleString()
          });
        }
        
        setError(null);
      } else {
        setError('No tenant data received from the server.');
      }
    } catch (err) {
      console.error('Error loading tenant details:', err);
      
      if (err.response?.status === 401) {
        setError('Authentication failed. Please log in again as a superadmin.');
      } else if (err.response?.status === 404) {
        setError(`Tenant with ID ${id} not found.`);
      } else {
        setError(`Failed to load tenant details: ${err.message || 'Unknown error'}`);
      }
    } finally {
      setLoadingDetails(false);
    }
  };
  
  const fetchTenantFinancials = async () => {
    try {
      setLoadingFinancials(true);
      const response = await superAdminService.getTenantFinancials(id);
      setFinancialData(response.data);
    } catch (err) {
      console.error('Error loading financial data:', err);
      setError('Failed to load financial data');
    } finally {
      setLoadingFinancials(false);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const storedUser = JSON.parse(localStorage.getItem('authUser') || '{}');
        if (!storedUser || storedUser.role !== 'superadmin') {
          const user = await authService.getCurrentUser();
          
          if (!user || user.role !== 'superadmin') {
            setError('You must be logged in as a superadmin to access this page.');
            setLoading(false);
            return;
          }
        }
        
        const token = authService.getToken();
        if (!token) {
          setError('Authentication token not found. Please log in again.');
          setLoading(false);
          return;
        }
        
        setAuthChecked(true);
        
        await loadTenantDetailsOptimized();

        try {
          const plans = await stripeService.getSubscriptionPlansWithCache();
          console.log('Available subscription plans:', plans);
          if (Array.isArray(plans) && plans.length > 0) {
            setAvailablePlans(plans);
          }
        } catch (planError) {
          console.error('Error fetching subscription plans:', planError);
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Auth check error:", err);
        setError('Authentication error. Please try logging in again.');
        setLoading(false);
      }
    };
    
    checkAuth();
  }, [id]);

  useEffect(() => {
    if (tabValue === 5 && !financialData) {
      fetchTenantFinancials();
    }
  }, [tabValue, id]);

  const loadAdminData = async () => {
    try {
      setLoadingAdmins(true);
      const adminsResponse = await superAdminService.getTenantAdmins(id);
      setAdminUsers(adminsResponse);
    } catch (err) {
      console.error('Error loading admin data:', err);
    } finally {
      setLoadingAdmins(false);
    }
  };

  const handleApproveTenant = async () => {
    try {
      await superAdminService.approveTenant(id);
      setSuccessMessage('Tenant approved successfully');
      setOpenSnackbar(true);
      loadTenantDetailsOptimized();
    } catch (err) {
      setError('Failed to approve tenant. Please try again.');
      setOpenSnackbar(true);
      console.error('Error approving tenant:', err);
    }
  };

  const handleOpenSuspendDialog = () => {
    setSuspendReason('');
    setSuspendDialogOpen(true);
  };
  
  const handleSuspendTenant = async () => {
    try {
      if (suspendReason.trim()) {
        await superAdminService.suspendTenant(id, suspendReason);
        setSuccessMessage('Tenant suspended successfully');
        setOpenSnackbar(true);
        setSuspendDialogOpen(false);
        loadTenantDetailsOptimized();
      }
    } catch (err) {
      setError('Failed to suspend tenant. Please try again.');
      setOpenSnackbar(true);
      console.error('Error suspending tenant:', err);
    }
  };

  const handleReactivateTenant = async () => {
    try {
      await superAdminService.reactivateTenant(id);
      setSuccessMessage('Tenant reactivated successfully');
      setOpenSnackbar(true);
      loadTenantDetailsOptimized();
    } catch (err) {
      setError('Failed to reactivate tenant. Please try again.');
      setOpenSnackbar(true);
      console.error('Error reactivating tenant:', err);
    }
  };
  
  const handleOpenNotesDialog = () => {
    setAdminNotes(tenant?.adminNotes || '');
    setNotesDialogOpen(true);
  };
  
  const handleSaveNotes = async () => {
    try {
      await superAdminService.updateTenant(id, { adminNotes });
      setSuccessMessage('Notes updated successfully');
      setOpenSnackbar(true);
      setNotesDialogOpen(false);
      setTenant(prev => ({ ...prev, adminNotes }));
    } catch (err) {
      setError('Failed to update notes. Please try again.');
      setOpenSnackbar(true);
      console.error('Error updating notes:', err);
    }
  };
  
  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteTenant = async () => {
    try {
      await superAdminService.deleteTenant(id);
      setSuccessMessage('Organization deleted successfully');
      setOpenSnackbar(true);
      setDeleteDialogOpen(false);
      setTimeout(() => {
        navigate('/superadmin/tenants');
      }, 1500);
    } catch (err) {
      setError('Failed to delete organization. Please try again.');
      setOpenSnackbar(true);
      console.error('Error deleting tenant:', err);
    }
  };
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    
    if (newValue === 3 && (!adminUsers || adminUsers.length === 0)) {
      loadAdminData();
    }
    
    if (newValue === 5 && !financialData) {
      fetchTenantFinancials();
    }
  };
  
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };
  
  const handleRefreshData = async () => {
    try {
      sessionStorage.removeItem(`tenant_details_${id}`);
    } catch (err) {
      console.warn('Could not clear tenant details cache:', err);
    }
    
    await loadTenantDetailsOptimized();
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };
  
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };
  
  const calculateUsagePercentage = (used, limit) => {
    if (!used || !limit) return 0;
    return Math.min(Math.round((used / limit) * 100), 100);
  };

  const handleOpenResetPasswordDialog = (admin) => {
    setAdminToReset(admin);
    setResetPasswordDialogOpen(true);
    setTempPassword('');
  };
  
  const handleConfirmResetPassword = async () => {
    try {
      const result = await superAdminService.resetTenantAdminPassword(id, adminToReset.email);
      setTempPassword(result.tempPassword);
      setSuccessMessage(`Password reset successfully for ${adminToReset.email}`);
      setOpenSnackbar(true);
    } catch (err) {
      setError('Failed to reset password');
      setOpenSnackbar(true);
      setResetPasswordDialogOpen(false);
    }
  };
  
  const handleOpenStatusChangeDialog = (admin) => {
    setAdminToChangeStatus(admin);
    setNewAdminStatus(admin.status || 'active');
    setAdminStatusDialogOpen(true);
  };
  
  const handleConfirmStatusChange = async () => {
    try {
      await superAdminService.updateTenantAdmin(id, adminToChangeStatus._id, {
        status: newAdminStatus
      });
      
      setSuccessMessage(`Admin status updated successfully`);
      setOpenSnackbar(true);
      setAdminStatusDialogOpen(false);
      
      const updatedAdmins = await superAdminService.getTenantAdmins(id);
      if (updatedAdmins) {
        setAdminUsers(updatedAdmins);
      }
    } catch (err) {
      setError('Failed to update admin status');
      setOpenSnackbar(true);
      setAdminStatusDialogOpen(false);
    }
  };

  const handleOpenPlanDialog = () => {
    setSelectedPlan(tenant?.plan || 'basic');
    setPlanChangeNote('');
    setPlanDialogOpen(true);
  };

  const handleChangePlan = async () => {
    try {
      if (selectedPlan && selectedPlan !== tenant?.plan) {
        const planChangeData = {
          plan: selectedPlan,
          note: planChangeNote
        };

        await stripeService.changeTenantPlan(id, planChangeData);
        
        setSuccessMessage(`Plan successfully updated from ${tenant.plan} to ${selectedPlan}`);
        setOpenSnackbar(true);
        setPlanDialogOpen(false);
        loadTenantDetailsOptimized();
      }
    } catch (err) {
      setError(`Failed to update subscription plan: ${err.message}`);
      setOpenSnackbar(true);
      console.error('Error updating plan:', err);
    }
  };

  const getUsageStatus = (percentage) => {
    if (percentage >= 90) return <Chip label="Critical" color="error" size="small" />;
    if (percentage >= 70) return <Chip label="High" color="warning" size="small" />;
    return <Chip label="Normal" color="success" size="small" />;
  };

  const handlePrintMenuOpen = (event) => {
    setPrintMenuAnchor(event.currentTarget);
  };

  const handlePrintMenuClose = () => {
    setPrintMenuAnchor(null);
  };

  const handleSelectPrintReport = (type) => {
    setPrintReportType(type);
    setPrintDialogOpen(true);
    handlePrintMenuClose();
  };

  const handlePrintReport = async () => {
    try {
      setIsPrinting(true);
      
      if (!financialReportRef.current) {
        throw new Error('Report component not found');
      }

      const fileName = `${tenant?.name?.replace(/\s+/g, '_') || 'organization'}_financial_report`;
      
      await generatePdfFromElement(
        financialReportRef.current,
        fileName,
        {
          onLoadingStart: () => setIsPrinting(true),
          onLoadingComplete: () => {
            setIsPrinting(false);
            setPrintDialogOpen(false);
            setSuccessMessage('Financial report generated successfully!');
            setOpenSnackbar(true);
          },
          onError: (error) => {
            console.error('PDF generation error:', error);
            setIsPrinting(false);
            setError('Failed to generate financial report: ' + error.message);
            setOpenSnackbar(true);
          }
        }
      );
    } catch (error) {
      console.error('Error printing report:', error);
      setIsPrinting(false);
      setError('Failed to generate financial report: ' + error.message);
      setOpenSnackbar(true);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box my={4}>
          <Skeleton variant="rectangular" height={40} width={300} />
          <Skeleton variant="text" sx={{ mt: 1 }} />
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Skeleton variant="rectangular" height={200} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={400} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={400} />
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (!authChecked && error) {
    return (
      <Container maxWidth="xl">
        <Alert 
          severity="error" 
          sx={{ mt: 4, mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/superadmin/login')}>
              Go to Login
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  if (error && !tenant) {
    return (
      <Container maxWidth="xl">
        <Alert 
          severity="error" 
          sx={{ mt: 4, mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/superadmin/tenants')}>
              Back to Tenants
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  if (!tenant) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>Tenant not found</Typography>
          <Button 
            variant="contained"
            onClick={() => navigate('/superadmin/tenants')}
            startIcon={<ArrowBackIcon />}
          >
            Back to Tenants
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box my={4}>
        <Box sx={{ mb: 2 }}>
          <Breadcrumbs>
            <Link 
              to="/superadmin/tenants"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              Organizations
            </Link>
            <Typography color="text.primary">{tenant?.name || 'Unknown Organization'}</Typography>
          </Breadcrumbs>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: 'primary.main',
                fontSize: '1.5rem',
                mr: 2
              }}
            >
              {tenant?.name ? tenant.name.substring(0, 2).toUpperCase() : 'TN'}
            </Avatar>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="h4" component="h1" fontWeight="bold">
                  {tenant?.name || 'Unknown Organization'}
                </Typography>
                <StatusBadge status={tenant?.status || 'unknown'} />
              </Box>
              <Typography variant="body1" color="text.secondary">
                {tenant?.subdomain ? `${tenant.subdomain}.${window.location.hostname}` : 'No subdomain'}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<ArrowBackIcon />}
              component={Link}
              to="/superadmin/tenants"
              size="medium"
            >
              Back to List
            </Button>
            
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PrintIcon />}
              onClick={handlePrintMenuOpen}
              size="medium"
            >
              Financial Report
            </Button>
            
            <Button
              variant="outlined"
              color="primary"
              startIcon={<EditIcon />}
              component={Link}
              to={`/superadmin/tenants/${id}/edit`}
              size="medium"
            >
              Edit
            </Button>
            
            {tenant.status === 'pending' && (
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={handleApproveTenant}
                size="medium"
              >
                Approve
              </Button>
            )}
            
            {tenant.status !== 'suspended' && (
              <Button
                variant="contained"
                color="error"
                startIcon={<PauseCircleOutlineIcon />}
                onClick={handleOpenSuspendDialog}
              >
                Suspend
              </Button>
            )}
            
            {tenant.status === 'suspended' && (
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={handleReactivateTenant}
              >
                Reactivate
              </Button>
            )}
            <Button
              variant="outlined"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={handleRefreshData}
              size="medium"
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Box>
      
      <Snackbar open={openSnackbar} autoHideDuration={3000} onClose={handleCloseSnackbar}>
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={error ? "error" : "success"} 
          variant="filled"
        >
          {error || successMessage}
        </Alert>
      </Snackbar>
      
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ height: '100%', borderLeft: '4px solid #3f51b5' }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'rgba(63, 81, 181, 0.1)', color: '#3f51b5' }}>
                  <PersonIcon />
                </Avatar>
                <Box ml={2}>
                  <Typography variant="body2" color="text.secondary">Total Members</Typography>
                  <Typography variant="h5">{memberCount || 'N/A'}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ height: '100%', borderLeft: '4px solid #00bcd4' }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'rgba(0, 188, 212, 0.1)', color: '#00bcd4' }}>
                  <StorageIcon />
                </Avatar>
                <Box ml={2}>
                  <Typography variant="body2" color="text.secondary">Storage Used</Typography>
                  <Typography variant="h5">
                    {usageData?.storage?.used ? `${(usageData.storage.used / 1024 / 1024).toFixed(2)} MB` : 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ 
            height: '100%', 
            borderLeft: `4px solid ${getPlanColors(tenant?.plan || 'basic').main}`
          }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ 
                  bgcolor: getPlanColors(tenant?.plan || 'basic').light, 
                  color: getPlanColors(tenant?.plan || 'basic').main 
                }}>
                  <MoneyIcon />
                </Avatar>
                <Box ml={2}>
                  <Typography variant="body2" color="text.secondary">Current Plan</Typography>
                  <Typography variant="h5" sx={{ color: getPlanColors(tenant?.plan || 'basic').text }}>
                    {getPlanName(tenant?.plan || 'basic')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ height: '100%', borderLeft: '4px solid #673ab7' }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'rgba(103, 58, 183, 0.1)', color: '#673ab7' }}>
                  <BusinessIcon />
                </Avatar>
                <Box ml={2}>
                  <Typography variant="body2" color="text.secondary">Created</Typography>
                  <Typography variant="h5">
                    {tenant?.createdAt ? new Date(tenant.createdAt).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {(tenant?.adminNotes || tenant?.status === 'suspended') && (
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            mb: 4, 
            borderRadius: 2, 
            borderLeft: tenant?.status === 'suspended' ? '4px solid #f44336' : '4px solid #ff9800'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Typography variant="h6" gutterBottom>
              {tenant?.status === 'suspended' ? 'Suspension Notice' : 'Admin Notes'}
            </Typography>
            <Button 
              size="small" 
              color="primary"
              startIcon={<EditIcon />} 
              onClick={handleOpenNotesDialog}
              variant="text"
            >
              Edit Notes
            </Button>
          </Box>
          
          {tenant?.status === 'suspended' && tenant?.suspensionReason && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Suspension Reason:</Typography>
              <Typography variant="body2">{tenant.suspensionReason}</Typography>
            </Alert>
          )}
          
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {tenant?.adminNotes || 'No additional notes.'}
          </Typography>
        </Paper>
      )}

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Overview" />
            <Tab label="Subscription" />
            <Tab label="Usage" />
            <Tab label="Administrators" />
            <Tab label="Settings" />
            <Tab label="Financials" />
          </Tabs>
        </Box>
        
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom>Organization Information</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Organization Name</Typography>
                    <Typography variant="body1">{tenant?.name || 'N/A'}</Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Subdomain</Typography>
                    <Typography variant="body1">{tenant?.subdomain || 'N/A'}</Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                    <StatusBadge status={tenant?.status || 'unknown'} />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Created Date</Typography>
                    <Typography variant="body1">{formatDate(tenant?.createdAt)}</Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Primary Contact</Typography>
                    <Typography variant="body1">{tenant?.adminName || 'N/A'}</Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Contact Email</Typography>
                    <Typography variant="body1">{tenant?.adminEmail || 'N/A'}</Typography>
                  </Grid>
                  
                  {tenant?.phone && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                      <Typography variant="body1">{tenant.phone}</Typography>
                    </Grid>
                  )}
                  
                  {tenant?.website && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Website</Typography>
                      <Typography variant="body1">{tenant.website}</Typography>
                    </Grid>
                  )}
                  
                  {tenant?.address && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">Address</Typography>
                      <Typography variant="body1">{tenant.address}</Typography>
                    </Grid>
                  )}
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Current Plan</Typography>
                    {tenant?.plan && (
                      <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: getPlanColors(tenant.plan).main,
                            mr: 1
                          }}
                        />
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: getPlanColors(tenant.plan).text,
                            fontWeight: 'medium' 
                          }}
                        >
                          {getPlanName(tenant.plan)}
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom>Subscription Information</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Current Plan</Typography>
                    <Typography variant="body1">
                      {(() => {
                        const tenantPlanId = tenant?.plan || 'basic';
                        const matchedPlan = availablePlans.find(p => p.planId === tenantPlanId);
                        
                        if (matchedPlan) {
                          return matchedPlan.name;
                        } else {
                          return subscriptionData?.plan?.name || getPlanName(tenantPlanId);
                        }
                      })()}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Billing Cycle</Typography>
                    <Typography variant="body1">
                      {(() => {
                        const tenantPlanId = tenant?.plan || 'basic';
                        const matchedPlan = availablePlans.find(p => p.planId === tenantPlanId);
                        
                        if (matchedPlan) {
                          return matchedPlan.interval === 'month' ? 'Monthly' : 
                                 matchedPlan.interval === 'year' ? 'Yearly' : 
                                 matchedPlan.interval.charAt(0).toUpperCase() + matchedPlan.interval.slice(1) + 'ly';
                        } else {
                          return subscriptionData?.billingCycle ? 
                            (subscriptionData.billingCycle.charAt(0).toUpperCase() + subscriptionData.billingCycle.slice(1)) : 
                            (subscriptionData?.interval ? 
                              (subscriptionData.interval.charAt(0).toUpperCase() + subscriptionData.interval.slice(1) + 'ly') : 
                              'Monthly');
                        }
                      })()}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Next Billing Date</Typography>
                    <Typography variant="body1">
                      {subscriptionData?.nextBillingDate ? 
                        formatDate(subscriptionData.nextBillingDate) : 
                        (subscriptionData?.currentPeriodEnd ? 
                          formatDate(subscriptionData.currentPeriodEnd) : 
                          'N/A')}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Amount</Typography>
                    <Typography variant="body1">
                      {(() => {
                        const tenantPlanId = tenant?.plan || 'basic';
                        const matchedPlan = availablePlans.find(p => p.planId === tenantPlanId);
                        
                        if (matchedPlan) {
                          return `${formatCurrency(matchedPlan.price)}/${matchedPlan.interval}`;
                        } else {
                          return subscriptionData?.price ? 
                            `${formatCurrency(subscriptionData.price)}/${subscriptionData?.billingCycle || subscriptionData?.interval || 'month'}` : 
                            'N/A';
                        }
                      })()}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                    <Typography variant="body1">
                      {subscriptionData?.status ? 
                        <Chip 
                          size="small" 
                          label={subscriptionData.status.replace('_', ' ')}
                          color={
                            subscriptionData.status === 'active' ? 'success' : 
                            ['past_due', 'incomplete'].includes(subscriptionData.status) ? 'warning' : 
                            ['canceled', 'unpaid'].includes(subscriptionData.status) ? 'error' : 'default'
                          }
                          variant="outlined" 
                        /> : 
                        'N/A'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">Member Limit</Typography>
                    <Typography variant="body1">
                      {(() => {
                        const tenantPlanId = tenant?.plan || 'basic';
                        const matchedPlan = availablePlans.find(p => p.planId === tenantPlanId);
                        
                        let memberLimit = null;
                        if (matchedPlan?.features) {
                          const memberFeature = matchedPlan.features.find(f => 
                            f.toLowerCase().includes('member') && f.toLowerCase().includes('up to'));
                          
                          if (memberFeature) {
                            const matches = memberFeature.match(/up to (\d+) members/i);
                            if (matches && matches[1]) {
                              memberLimit = parseInt(matches[1]);
                            }
                          }
                        }
                        
                        if (!memberLimit) {
                          memberLimit = subscriptionData?.plan?.limits?.members || 
                                     tenant?.plan?.limits?.members ||
                                     (tenantPlanId === 'basic' ? 50 : 
                                      tenantPlanId === 'professional' ? 500 : 
                                      tenantPlanId === 'enterprise' ? 2000 : null);
                        }
                        
                        return memberLimit ? 
                          `${memberCount || 0} / ${memberLimit}` : 
                          'Unlimited';
                      })()}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          {subscriptionData ? (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Current Plan</Typography>
                    
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<EditIcon />}
                      onClick={handleOpenPlanDialog}
                    >
                      Change Plan
                    </Button>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              backgroundColor: getPlanColors(tenant?.plan || 'basic').main,
                              mr: 1
                            }}
                          />
                          {(() => {
                            const tenantPlanId = tenant?.plan || 'basic';
                            const matchedPlan = availablePlans.find(p => p.planId === tenantPlanId);
                            
                            if (matchedPlan) {
                              return matchedPlan.name;
                            } else {
                              return subscriptionData?.plan?.name || getPlanName(tenantPlanId);
                            }
                          })()}
                        </Box>
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {(() => {
                          const tenantPlanId = tenant?.plan || 'basic';
                          const matchedPlan = availablePlans.find(p => p.planId === tenantPlanId);
                          
                          if (matchedPlan && matchedPlan.description) {
                            return matchedPlan.description;
                          } else {
                            return subscriptionData?.plan?.description || 
                                 `Standard features for ${getPlanName(tenant?.plan || 'basic')} with support and storage options`;
                          }
                        })()}
                      </Typography>
                        
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Billing Details
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <ReceiptIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {(() => {
                              const tenantPlanId = tenant?.plan || 'basic';
                              const matchedPlan = availablePlans.find(p => p.planId === tenantPlanId);
                              
                              if (matchedPlan) {
                                return matchedPlan.interval === 'month' ? 'Monthly' : 
                                       matchedPlan.interval === 'year' ? 'Yearly' : 
                                       matchedPlan.interval.charAt(0).toUpperCase() + matchedPlan.interval.slice(1) + 'ly';
                              } else {
                                return subscriptionData?.billingCycle || (subscriptionData?.interval ? 
                                  subscriptionData.interval + 'ly' : 'Monthly');
                              }
                            })()}
                            {' billing'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <DateRangeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {subscriptionData?.status === 'canceled' ? 
                              'Subscription canceled' : 
                              `Next payment: ${formatDate(subscriptionData?.nextBillingDate || subscriptionData?.currentPeriodEnd)}`}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <MoneyIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {(() => {
                              const tenantPlanId = tenant?.plan || 'basic';
                              const matchedPlan = availablePlans.find(p => p.planId === tenantPlanId);
                              
                              if (matchedPlan) {
                                return `${formatCurrency(matchedPlan.price)}/${matchedPlan.interval}`;
                              } else {
                                return subscriptionData?.price ? 
                                  `${formatCurrency(subscriptionData.price)}/${subscriptionData?.billingCycle || 
                                    (subscriptionData?.interval ? subscriptionData.interval : 'month')}` : 
                                  'Free';
                              }
                            })()}
                          </Typography>
                        </Box>
                        {subscriptionData?.cancelAtPeriodEnd && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            <CancelIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
                            <Typography variant="body2" color="error.main">
                              Will cancel at the end of billing period
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Plan Limits
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2">Members</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {(() => {
                              const tenantPlanId = tenant?.plan || 'basic';
                              const matchedPlan = availablePlans.find(p => p.planId === tenantPlanId);
                              
                              let memberLimit = null;
                              if (matchedPlan?.features) {
                                const memberFeature = matchedPlan.features.find(f => 
                                  f.toLowerCase().includes('member') && f.toLowerCase().includes('up to'));
                                
                                if (memberFeature) {
                                  const matches = memberFeature.match(/up to (\d+) members/i);
                                  if (matches && matches[1]) {
                                    memberLimit = parseInt(matches[1]);
                                  }
                                }
                              }
                              
                              if (!memberLimit) {
                                memberLimit = subscriptionData?.plan?.limits?.members || 
                                           tenant?.plan?.limits?.members ||
                                           (tenantPlanId === 'basic' ? 50 : 
                                            tenantPlanId === 'professional' ? 500 : 
                                            tenantPlanId === 'enterprise' ? 2000 : null);
                              }
                              
                              return memberLimit ? 
                                `${memberCount || 0} / ${memberLimit}` : 
                                `${memberCount || 0} / Unlimited`;
                            })()}
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={(() => {
                            const tenantPlanId = tenant?.plan || 'basic';
                            const matchedPlan = availablePlans.find(p => p.planId === tenantPlanId);
                            
                            let memberLimit = null;
                            if (matchedPlan?.features) {
                              const memberFeature = matchedPlan.features.find(f => 
                                f.toLowerCase().includes('member') && f.toLowerCase().includes('up to'));
                              
                              if (memberFeature) {
                                const matches = memberFeature.match(/up to (\d+) members/i);
                                if (matches && matches[1]) {
                                  memberLimit = parseInt(matches[1]);
                                }
                              }
                            }
                            
                            if (!memberLimit) {
                              memberLimit = subscriptionData?.plan?.limits?.members || 
                                         tenant?.plan?.limits?.members ||
                                         (tenantPlanId === 'basic' ? 50 : 
                                          tenantPlanId === 'professional' ? 500 : 
                                          tenantPlanId === 'enterprise' ? 2000 : 1000);
                            }
                            
                            return calculateUsagePercentage(memberCount, memberLimit);
                          })()} 
                          sx={{ height: 6, borderRadius: 1 }}
                        />
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2">Storage</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {usageData?.storage?.used && usageData?.storage?.limit ? 
                              `${(usageData.storage.used / 1024 / 1024).toFixed(1)} MB / ${(usageData.storage.limit / 1024 / 1024).toFixed(1)} MB` : 
                              'Unlimited'}
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={calculateUsagePercentage(
                            usageData?.storage?.used, 
                            usageData?.storage?.limit
                          )} 
                          sx={{ height: 6, borderRadius: 1 }}
                        />
                      </Box>
                      
                      {(() => {
                        const tenantPlanId = tenant?.plan || 'basic';
                        const matchedPlan = availablePlans.find(p => p.planId === tenantPlanId);
                        
                        if (matchedPlan?.features?.length) {
                          return (
                            <Box sx={{ mt: 3 }}>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Included Features
                              </Typography>
                              <List dense disablePadding>
                                {matchedPlan.features.map((feature, idx) => (
                                  <ListItem key={idx} disableGutters sx={{ py: 0.5 }}>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                      <CheckCircleIcon color="primary" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary={feature} />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          );
                        }
                        return null;
                      })()}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                <Paper elevation={1} sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>Payment History</Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  {subscriptionData.subscriptionHistory?.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {subscriptionData.subscriptionHistory.map((payment, index) => (
                            <TableRow key={index} hover>
                              <TableCell>{formatDate(payment.date)}</TableCell>
                              <TableCell>{payment.description || `${payment.plan} subscription`}</TableCell>
                              <TableCell>{formatCurrency(payment.amount)}</TableCell>
                              <TableCell>
                                <Chip 
                                  size="small" 
                                  label={payment.status || 'completed'} 
                                  color={payment.status === 'failed' ? 'error' : 'success'} 
                                  variant="outlined" 
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                      No payment history available
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          ) : (
            <Typography variant="body1" color="text.secondary">
              {loading ? 'Loading subscription details...' : 'Subscription information is not available'}
            </Typography>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {usageData ? (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" gutterBottom>Resource Usage</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Last updated: {usageData.lastUpdated ? formatDate(usageData.lastUpdated) : 'Never'}
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />
                  
                  <Grid container spacing={4}>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>Storage</Typography>
                        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                          <CircularProgress
                            variant="determinate"
                            value={usageData.storage?.percentage || 0}
                            size={100}
                            thickness={5}
                            sx={{ color: 'primary.main' }}
                          />
                          <Box
                            sx={{
                              top: 0,
                              left: 0,
                              bottom: 0,
                              right: 0,
                              position: 'absolute',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography variant="h6" component="div" color="primary.main">
                              {usageData.storage?.percentage || 0}%
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {usageData.storage?.used && usageData.storage?.limit ? 
                            `${(usageData.storage.used / 1024 / 1024).toFixed(1)} MB / ${(usageData.storage.limit / 1024 / 1024).toFixed(1)} MB` : 
                            'Unlimited'}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>Members</Typography>
                        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                          <CircularProgress
                            variant="determinate"
                            value={usageData.members?.percentage || 0}
                            size={100}
                            thickness={5}
                            sx={{ color: 'success.main' }}
                          />
                          <Box
                            sx={{
                              top: 0,
                              left: 0,
                              bottom: 0,
                              right: 0,
                              position: 'absolute',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography variant="h6" component="div" color="success.main">
                              {usageData.members?.percentage || 0}%
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {memberCount || 0} / {usageData.members?.limit || 'Unlimited'}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>Administrators</Typography>
                        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                          <CircularProgress
                            variant="determinate"
                            value={usageData.admins?.percentage || 0}
                            size={100}
                            thickness={5}
                            sx={{ color: 'secondary.main' }}
                          />
                          <Box
                            sx={{
                              top: 0,
                              left: 0,
                              bottom: 0,
                              right: 0,
                              position: 'absolute',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography variant="h6" component="div" color="secondary.main">
                              {usageData.admins?.percentage || 0}%
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {usageData.admins?.used || 0} / {usageData.admins?.limit || 'Unlimited'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" gutterBottom>Detailed Usage Metrics</Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Resource</TableCell>
                            <TableCell align="right">Used</TableCell>
                            <TableCell align="right">Limit</TableCell>
                            <TableCell align="right">Usage</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow hover>
                            <TableCell component="th" scope="row">
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <PersonIcon fontSize="small" sx={{ mr: 1, color: 'success.main' }} />
                                Members
                              </Box>
                            </TableCell>
                            <TableCell align="right">{memberCount || 0}</TableCell>
                            <TableCell align="right">{usageData.members?.limit || 'Unlimited'}</TableCell>
                            <TableCell align="right">
                              <LinearProgress
                                variant="determinate"
                                value={usageData.members?.percentage || 0}
                                sx={{ height: 8, width: 100, borderRadius: 1, display: 'inline-block' }}
                              />
                            </TableCell>
                            <TableCell>
                              {getUsageStatus(usageData.members?.percentage || 0)}
                            </TableCell>
                          </TableRow>

                          <TableRow hover>
                            <TableCell component="th" scope="row">
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <SecurityIcon fontSize="small" sx={{ mr: 1, color: 'secondary.main' }} />
                                Administrators
                              </Box>
                            </TableCell>
                            <TableCell align="right">{usageData.admins?.used || 0}</TableCell>
                            <TableCell align="right">{usageData.admins?.limit || 'Unlimited'}</TableCell>
                            <TableCell align="right">
                              <LinearProgress
                                variant="determinate"
                                value={usageData.admins?.percentage || 0}
                                sx={{ height: 8, width: 100, borderRadius: 1, display: 'inline-block' }}
                              />
                            </TableCell>
                            <TableCell>
                              {getUsageStatus(usageData.admins?.percentage || 0)}
                            </TableCell>
                          </TableRow>

                          <TableRow hover>
                            <TableCell component="th" scope="row">
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <StorageIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                                Storage
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              {usageData.storage?.used ? `${(usageData.storage.used / 1024 / 1024).toFixed(1)} MB` : 'N/A'}
                            </TableCell>
                            <TableCell align="right">
                              {usageData.storage?.limit ? `${(usageData.storage.limit / 1024 / 1024).toFixed(1)} MB` : 'Unlimited'}
                            </TableCell>
                            <TableCell align="right">
                              <LinearProgress
                                variant="determinate"
                                value={usageData.storage?.percentage || 0}
                                sx={{ height: 8, width: 100, borderRadius: 1, display: 'inline-block' }}
                              />
                            </TableCell>
                            <TableCell>
                              {getUsageStatus(usageData.storage?.percentage || 0)}
                            </TableCell>
                          </TableRow>

                          {Object.entries(usageData).filter(([key]) => 
                            !['members', 'admins', 'storage', 'lastUpdated'].includes(key)
                          ).map(([key, value]) => {
                            if (typeof value === 'object' && value !== null && value.used !== undefined) {
                              return (
                                <TableRow key={key} hover>
                                  <TableCell component="th" scope="row" sx={{ textTransform: 'capitalize' }}>
                                    {key}
                                  </TableCell>
                                  <TableCell align="right">{value.used}</TableCell>
                                  <TableCell align="right">{value.limit || 'Unlimited'}</TableCell>
                                  <TableCell align="right">
                                    <LinearProgress
                                      variant="determinate"
                                      value={value.percentage || 0}
                                      sx={{ height: 8, width: 100, borderRadius: 1, display: 'inline-block' }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {getUsageStatus(value.percentage || 0)}
                                  </TableCell>
                                </TableRow>
                              );
                            }
                            return null;
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary" paragraph>
                {loadingDetails ? 'Loading usage statistics...' : 'Usage statistics are not available for this tenant.'}
              </Typography>
              <Button 
                startIcon={<RefreshIcon />} 
                variant="outlined" 
                onClick={handleRefreshData}
                disabled={loadingDetails}
              >
                {loadingDetails ? 'Loading...' : 'Refresh Data'}
              </Button>
            </Box>
          )}
        </TabPanel>
        
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Administrators</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => navigate(`/superadmin/tenants/${id}/add-admin`)}
                  >
                    Add Admin
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                {adminUsers && adminUsers.length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Last Login</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {adminUsers.map((admin) => (
                          <TableRow key={admin._id} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar sx={{ mr: 2, bgcolor: 'primary.light' }}>
                                  {admin.firstName?.[0]}
                                  {admin.lastName?.[0]}
                                </Avatar>
                                <div>
                                  <Typography variant="body2">
                                    {admin.firstName} {admin.lastName}
                                  </Typography>
                                  {tenant?.adminId && tenant.adminId === admin._id && (
                                    <Chip 
                                      label="Primary Admin" 
                                      size="small" 
                                      color="primary" 
                                      variant="outlined"
                                      sx={{ mt: 0.5 }}
                                    />
                                  )}
                                </div>
                              </Box>
                            </TableCell>
                            <TableCell>{admin.email}</TableCell>
                            <TableCell>
                              <StatusBadge status={admin.status || 'active'} />
                            </TableCell>
                            <TableCell>
                              {admin.lastLogin ? formatDate(admin.lastLogin) : 'Never'}
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Tooltip title="Reset Password">
                                  <IconButton 
                                    size="small" 
                                    color="warning"
                                    onClick={() => handleOpenResetPasswordDialog(admin)}
                                  >
                                    <SecurityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Change Status">
                                  <IconButton 
                                    size="small" 
                                    color="info"
                                    onClick={() => handleOpenStatusChangeDialog(admin)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      No administrators found for this tenant
                    </Typography>
                    <Button
                      variant="contained"
                      sx={{ mt: 2 }}
                      startIcon={<AddIcon />}
                      onClick={() => navigate(`/superadmin/tenants/${id}/add-admin`)}
                    >
                      Add First Admin
                    </Button>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>
        
        <TabPanel value={tabValue} index={4}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Organization Settings</Typography>
                <Divider sx={{ mb: 3 }} />
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={tenant?.settings?.allowUserRegistration ?? true}
                          onChange={(e) => {
                            const newSettings = {
                              ...(tenant?.settings || {}),
                              allowUserRegistration: e.target.checked
                            };
                            superAdminService.updateTenant(id, { settings: newSettings })
                              .then(() => {
                                setTenant(prev => ({...prev, settings: newSettings}));
                                setSuccessMessage('Settings updated successfully');
                                setOpenSnackbar(true);
                              })
                              .catch(err => {
                                setError('Failed to update settings');
                                setOpenSnackbar(true);
                              });
                          }}
                        />
                      }
                      label="Allow Self-Registration"
                    />
                    <Typography variant="body2" color="text.secondary">
                      When enabled, users can register their own accounts in this organization
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={tenant?.settings?.requireEmailVerification ?? true}
                          onChange={(e) => {
                            const newSettings = {
                              ...(tenant?.settings || {}),
                              requireEmailVerification: e.target.checked
                            };
                            superAdminService.updateTenant(id, { settings: newSettings })
                              .then(() => {
                                setTenant(prev => ({...prev, settings: newSettings}));
                                setSuccessMessage('Settings updated successfully');
                                setOpenSnackbar(true);
                              })
                              .catch(err => {
                                setError('Failed to update settings');
                                setOpenSnackbar(true);
                              });
                          }}
                        />
                      }
                      label="Require Email Verification"
                    />
                    <Typography variant="body2" color="text.secondary">
                      Users must verify their email before accessing the platform
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>Domain Settings</Typography>
                <Divider sx={{ mb: 3 }} />
                
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Custom Domain"
                      value={tenant?.customDomain || ''}
                      placeholder="yourdomain.com"
                      helperText="Enter a custom domain for this organization"
                      onChange={(e) => {
                        superAdminService.updateTenant(id, { customDomain: e.target.value })
                          .then(() => {
                            setTenant(prev => ({...prev, customDomain: e.target.value}));
                            setSuccessMessage('Domain settings updated successfully');
                            setOpenSnackbar(true);
                          })
                          .catch(err => {
                            setError('Failed to update domain settings');
                            setOpenSnackbar(true);
                          });
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Financial Overview</Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<PrintIcon />}
                    onClick={handlePrintMenuOpen}
                  >
                    Generate Report
                  </Button>
                </Box>
                <Divider sx={{ mb: 3 }} />
                
                {loadingFinancials ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : financialData ? (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Card elevation={2} sx={{ p: 2, borderLeft: '4px solid #4caf50' }}>
                        <Typography variant="subtitle2" color="text.secondary">Total Revenue</Typography>
                        <Typography variant="h4" sx={{ my: 1 }}>
                          {formatCurrency(financialData.totalRevenue || 0)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Lifetime subscription payments
                        </Typography>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Card elevation={2} sx={{ p: 2, borderLeft: '4px solid #2196f3' }}>
                        <Typography variant="subtitle2" color="text.secondary">Member Payments</Typography>
                        <Typography variant="h4" sx={{ my: 1 }}>
                          {formatCurrency(financialData.memberPayments || 0)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total payments collected by organization
                        </Typography>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Card elevation={2} sx={{ p: 2, borderLeft: '4px solid #ff9800' }}>
                        <Typography variant="subtitle2" color="text.secondary">Outstanding Debts</Typography>
                        <Typography variant="h4" sx={{ my: 1 }}>
                          {formatCurrency(financialData.outstandingDebts || 0)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total debts owed by members
                        </Typography>
                      </Card>
                    </Grid>
                    
                    {financialData.subscriptionHistory && financialData.subscriptionHistory.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Subscription History</Typography>
                        <TableContainer component={Paper} variant="outlined">
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Plan</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {financialData.subscriptionHistory.map((item, index) => (
                                <TableRow key={index}>
                                  <TableCell>{formatDate(item.date)}</TableCell>
                                  <TableCell>{item.plan || 'N/A'}</TableCell>
                                  <TableCell>{formatCurrency(item.amount || 0)}</TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={item.status || 'completed'} 
                                      color={item.status === 'failed' ? 'error' : 'success'} 
                                      size="small"
                                      variant="outlined"
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    )}
                    
                    {financialData.recentTransactions && financialData.recentTransactions.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Recent Transactions</Typography>
                        <TableContainer component={Paper} variant="outlined">
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Amount</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {financialData.recentTransactions.map((item, index) => (
                                <TableRow key={index}>
                                  <TableCell>{formatDate(item.date)}</TableCell>
                                  <TableCell>{item.description || 'N/A'}</TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={item.type || 'payment'} 
                                      color={
                                        item.type === 'payment' ? 'success' : 
                                        item.type === 'refund' ? 'warning' : 
                                        'default'
                                      } 
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell>{formatCurrency(item.amount || 0)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    )}
                  </Grid>
                ) : (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary">No financial data available for this organization.</Typography>
                    <Button 
                      variant="contained" 
                      onClick={fetchTenantFinancials} 
                      sx={{ mt: 2 }}
                      startIcon={<RefreshIcon />}
                    >
                      Refresh Data
                    </Button>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
      
      <Paper 
        elevation={2} 
        sx={{ 
          mt: 4,
          p: 3, 
          borderRadius: 2, 
          borderLeft: '4px solid #f44336'
        }}
      >
        <Typography variant="h6" gutterBottom color="error">
          Danger Zone
        </Typography>
        <Typography variant="body2" paragraph>
          These actions are permanent and cannot be undone. Please be careful.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="error"
            onClick={handleOpenDeleteDialog}
            startIcon={<DeleteIcon />}
          >
            Delete Organization
          </Button>
        </Box>
      </Paper>
      
      <Dialog
        open={suspendDialogOpen}
        onClose={() => setSuspendDialogOpen(false)}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PauseCircleOutlineIcon color="error" />
            Suspend Organization
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please provide a reason for suspending this organization. This reason may be displayed to the tenant administrators.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Suspension Reason"
            fullWidth
            multiline
            rows={4}
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuspendDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSuspendTenant} 
            color="error"
            disabled={!suspendReason.trim()}
            startIcon={<PauseCircleOutlineIcon />}
          >
            Suspend
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog
        open={notesDialogOpen}
        onClose={() => setNotesDialogOpen(false)}
      >
        <DialogTitle>Admin Notes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            These notes are visible only to super administrators. Use this space for important information about this organization.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Notes"
            fullWidth
            multiline
            rows={6}
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveNotes} color="primary">
            Save Notes
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={resetPasswordDialogOpen}
        onClose={() => !tempPassword && setResetPasswordDialogOpen(false)}
      >
        <DialogTitle>
          {tempPassword ? 'Password Reset Complete' : 'Reset Admin Password'}
        </DialogTitle>
        <DialogContent>
          {tempPassword ? (
            <Box sx={{ mt: 1 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Password has been reset successfully
              </Alert>
              <Typography variant="subtitle1" gutterBottom>
                Temporary Password:
              </Typography>
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  mb: 2,
                  backgroundColor: 'background.paper',
                  fontFamily: 'monospace',
                  fontSize: '1.2rem',
                  textAlign: 'center'
                }}
              >
                {tempPassword}
              </Paper>
              <Typography variant="body2" color="text.secondary">
                The admin will be required to change this password on first login.
                Please communicate this password securely to the user.
              </Typography>
            </Box>
          ) : (
            <DialogContentText>
              Are you sure you want to reset the password for {adminToReset?.email}? 
              The admin will receive a temporary password and be required to change it on next login.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          {tempPassword ? (
            <Button 
              onClick={() => {
                setResetPasswordDialogOpen(false);
                setTempPassword('');
              }} 
              color="primary"
            >
              Close
            </Button>
          ) : (
            <>
              <Button 
                onClick={() => setResetPasswordDialogOpen(false)} 
                color="inherit"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleConfirmResetPassword} 
                color="warning"
                variant="contained"
              >
                Reset Password
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={adminStatusDialogOpen}
        onClose={() => setAdminStatusDialogOpen(false)}
      >
        <DialogTitle>Change Admin Status</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Update the status for administrator: {adminToChangeStatus?.firstName} {adminToChangeStatus?.lastName} ({adminToChangeStatus?.email})
          </DialogContentText>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="admin-status-select-label">Status</InputLabel>
            <Select
              labelId="admin-status-select-label"
              id="admin-status-select"
              value={newAdminStatus}
              label="Status"
              onChange={(e) => setNewAdminStatus(e.target.value)}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdminStatusDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmStatusChange} 
            color="primary"
            variant="contained"
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={planDialogOpen}
        onClose={() => setPlanDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MoneyIcon color="primary" />
            Change Subscription Plan
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select the new subscription plan for this organization. This will immediately change their features and usage limits.
          </DialogContentText>
          
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="plan-select-label">Subscription Plan</InputLabel>
            <Select
              labelId="plan-select-label"
              value={selectedPlan}
              onChange={(e) => setSelectedPlan(e.target.value)}
              label="Subscription Plan"
            >
              {availablePlans.length > 0 ? (
                availablePlans.map(plan => (
                  <MenuItem key={plan.id} value={plan.planId}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: getPlanColors(plan.planId).main,
                            mr: 1
                          }}
                        />
                        {plan.name}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD'
                        }).format(plan.price)}/{plan.interval}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))
              ) : (
                <>
                  <MenuItem value="basic">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: getPlanColors('basic').main,
                          mr: 1
                        }}
                      />
                      Basic Plan
                    </Box>
                  </MenuItem>
                  <MenuItem value="professional">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: getPlanColors('professional').main,
                          mr: 1
                        }}
                      />
                      Professional Plan
                    </Box>
                  </MenuItem>
                  <MenuItem value="enterprise">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: getPlanColors('enterprise').main,
                          mr: 1
                        }}
                      />
                      Enterprise Plan
                    </Box>
                  </MenuItem>
                  <MenuItem value="custom">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: getPlanColors('custom').main,
                          mr: 1
                        }}
                      />
                      Custom Plan
                    </Box>
                  </MenuItem>
                </>
              )}
            </Select>
          </FormControl>
          
          <TextField
            margin="dense"
            label="Notes (optional)"
            fullWidth
            multiline
            rows={3}
            value={planChangeNote}
            onChange={(e) => setPlanChangeNote(e.target.value)}
            helperText="Add any notes about this plan change (billing adjustments, etc.)"
          />
          
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="subtitle1" gutterBottom>Plan Change Summary</Typography>
            <Typography variant="body2">
              From: <strong>{getPlanName(tenant?.plan || 'basic')}</strong>
            </Typography>
            <Typography variant="body2">
              To: <strong>
                {availablePlans.length > 0 
                  ? availablePlans.find(p => p.planId === selectedPlan)?.name || getPlanName(selectedPlan) 
                  : getPlanName(selectedPlan)}
              </strong>
            </Typography>
            
            {availablePlans.length > 0 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Price: <strong>
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD'
                  }).format(availablePlans.find(p => p.planId === selectedPlan)?.price || 0)}
                  /{availablePlans.find(p => p.planId === selectedPlan)?.interval || 'month'}
                </strong>
              </Typography>
            )}
            
            {selectedPlan !== tenant?.plan && (
              <>
                {selectedPlan === 'professional' && tenant?.plan === 'basic' && (
                  <Alert severity="info" sx={{ mt: 2 }}>This is a plan upgrade. Additional features will be available immediately.</Alert>
                )}
                {selectedPlan === 'enterprise' && (tenant?.plan === 'basic' || tenant?.plan === 'professional') && (
                  <Alert severity="info" sx={{ mt: 2 }}>This is a plan upgrade. Additional features will be available immediately.</Alert>
                )}
                {((selectedPlan === 'basic' && tenant?.plan !== 'basic') || 
                   (selectedPlan === 'professional' && tenant?.plan === 'enterprise')) && (
                  <Alert severity="warning" sx={{ mt: 2 }}>This is a plan downgrade. Some features may become unavailable.</Alert>
                )}
              </>
            )}
            
            {availablePlans.length > 0 && availablePlans.find(p => p.planId === selectedPlan)?.features?.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Features:</Typography>
                <ul style={{ marginTop: 4, paddingLeft: 20 }}>
                  {availablePlans.find(p => p.planId === selectedPlan)?.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleChangePlan} 
            color="primary"
            variant="contained"
            disabled={selectedPlan === tenant?.plan}
            startIcon={<EditIcon />}
          >
            Change Plan
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon color="error" />
            Delete Organization
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this organization? This action cannot be undone and will remove all associated data.
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            All users, data, and settings for <strong>{tenant?.name}</strong> will be permanently deleted.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDeleteTenant} 
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Delete Organization
          </Button>
        </DialogActions>
      </Dialog>
      
      <Menu
        anchorEl={printMenuAnchor}
        open={Boolean(printMenuAnchor)}
        onClose={handlePrintMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { mt: 1, minWidth: 200, borderRadius: 2 }
        }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
          Select Report Type
        </Typography>
        <Divider sx={{ mb: 1 }} />
        <MenuItem onClick={() => handleSelectPrintReport('all')}>
          <PictureAsPdfIcon fontSize="small" sx={{ mr: 2, color: '#3f51b5' }} />
          Complete Financial Report
        </MenuItem>
        <MenuItem onClick={() => handleSelectPrintReport('revenue')}>
          <PictureAsPdfIcon fontSize="small" sx={{ mr: 2, color: '#4caf50' }} />
          Revenue Only
        </MenuItem>
        <MenuItem onClick={() => handleSelectPrintReport('transactions')}>
          <PictureAsPdfIcon fontSize="small" sx={{ mr: 2, color: '#ff9800' }} />
          Transactions Only
        </MenuItem>
      </Menu>
      
      <Dialog
        open={printDialogOpen}
        onClose={() => !isPrinting && setPrintDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'primary.light',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PrintIcon sx={{ mr: 1 }} />
            Organization Financial Report
          </Box>
          {!isPrinting && (
            <IconButton
              onClick={() => setPrintDialogOpen(false)}
              sx={{ color: 'white' }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent sx={{ 
          p: 0, 
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: '#f5f5f5',
          minHeight: '70vh'
        }}>
          <Box sx={{ my: 2, width: '100%', overflow: 'auto' }}>
            <OrgFinancialReport
              ref={financialReportRef}
              tenant={tenant}
              financialData={financialData}
              reportType={printReportType}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: 'background.paper' }}>
          <Button
            onClick={() => setPrintDialogOpen(false)}
            disabled={isPrinting}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handlePrintReport}
            disabled={isPrinting}
            variant="contained"
            startIcon={isPrinting ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
            color="primary"
          >
            {isPrinting ? 'Generating PDF...' : 'Download PDF Report'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isPrinting}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          bgcolor: 'rgba(0, 0, 0, 0.7)',
          p: 4,
          borderRadius: 2
        }}>
          <CircularProgress color="inherit" sx={{ mb: 2 }} />
          <Typography variant="h6">
            Generating PDF...
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Please wait, this may take a moment
          </Typography>
        </Box>
      </Backdrop>
      
    </Container>
  );
};

export default SuperAdminTenantDetailPage;